import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "framer-motion";
import { ShoppingBag } from "lucide-react";

export const HeroBannerCarousel = ({ banners }) => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: banners.length > 1, align: "start", dragFree: false });
  const pausedRef = React.useRef(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  useEffect(() => {
    if (!emblaApi || banners.length <= 1) return;
    const id = setInterval(() => {
      if (!pausedRef.current) emblaApi.scrollNext();
    }, 5200);
    return () => clearInterval(id);
  }, [emblaApi, banners.length]);

  const go = (banner) => {
    if (banner.product_id) navigate(`/pedido?producto=${banner.product_id}&quick=1`);
    else navigate("/pedido");
  };

  if (!banners?.length) return null;

  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-6 mt-8"
      onMouseEnter={() => { pausedRef.current = true; }}
      onMouseLeave={() => { pausedRef.current = false; }}>
      <div className="overflow-hidden rounded-[36px] shadow-[0_24px_50px_rgba(75,16,37,0.28)]" ref={emblaRef}>
        <div className="flex">
          {banners.map((banner, index) => (
            <div key={banner.id || index} className="flex-[0_0_100%] min-w-0">
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index === 0 ? 0.1 : 0 }}
                data-testid={`promo-card-${index}`}
                className="relative bg-gradient-to-br from-[#4B1025] to-[#761B38] overflow-hidden flex min-h-[230px] sm:min-h-[280px]">
                {banner.eyebrow && (
                  <div className="absolute top-4 sm:top-5 left-4 sm:left-5 z-20 bg-rosa text-vinoDark text-[10px] sm:text-[11px] font-bold uppercase tracking-widest px-3 sm:px-4 py-1.5 rounded-full max-w-[52%] truncate">
                    {banner.eyebrow}
                  </div>
                )}
                <div className={`relative z-10 w-[62%] sm:w-3/5 px-5 pb-7 ${banner.eyebrow ? "pt-20 sm:pt-24" : "pt-7 sm:pt-9"} sm:px-9 sm:pb-9 flex flex-col justify-center`}>
                  {banner.subtitle && <p className="font-serif italic text-crema/75 text-sm sm:text-base leading-tight line-clamp-2">{banner.subtitle}</p>}
                  <h2 className="font-serif text-3xl sm:text-5xl text-crema leading-[1.02] mt-1 line-clamp-3">{banner.title}</h2>
                  {banner.price_text && <p className="mt-2 sm:mt-3 font-serif text-2xl sm:text-4xl text-dorado leading-none">{banner.price_text}</p>}
                  <button onClick={() => go(banner)} data-testid={`promo-cta-${index}`}
                    className="mt-4 sm:mt-5 w-max bg-crema text-vinoDark font-semibold rounded-full px-5 sm:px-7 py-2.5 sm:py-3.5 text-sm sm:text-base shadow-lg active:scale-95 hover:-translate-y-0.5 transition-all flex items-center gap-2">
                    <ShoppingBag className="w-4 h-4" /> {banner.cta_label || "Pedir ahora"}
                  </button>
                </div>
                {banner.image && (
                  <div className="absolute right-0 top-0 h-full w-[42%] sm:w-2/5">
                    <img src={banner.image} alt={banner.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#4B1025] via-transparent to-transparent" />
                  </div>
                )}
              </motion.div>
            </div>
          ))}
        </div>
      </div>

      {banners.length > 1 && (
        <div className="flex justify-center gap-2 mt-4" aria-label="Banners destacados">
          {banners.map((banner, index) => (
            <button key={banner.id || index} onClick={() => emblaApi?.scrollTo(index)}
              aria-label={`Ver banner ${index + 1}`}
              className={`h-2 rounded-full transition-all ${selected === index ? "w-8 bg-vino" : "w-2 bg-rosa/60"}`}
              data-testid={`promo-dot-${index}`} />
          ))}
        </div>
      )}
    </section>
  );
};
